import BuildConfig from '$extensionSrc/buildConfig'
import { NO_TIMEOUT } from '$extensionSrc/consts/stageMachineConsts'
import { LinkedinSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import {
  MONTHS,
  OLDER_THAN_OPTION,
  getOlderThanMonthYear,
} from '$extensionSrc/utils/dateUtils'
import {
  LinkedinRecommendationKey,
  PLATFORM,
  StageType,
} from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'

import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import { AUTOMATION_STAGE_STATUSES } from '../genericAutomationStageStatuses'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getInitialValueBySettingKey } from '../utils'
import { getPostsIterator, deletePost } from './linkedinPostUtils'
import * as linkedinUrls from './linkedinUrls'

// Build result object for tracking deletion progress
function buildResultObject(
  totalCount: number,
  deletedCount: number,
  dateOption: OLDER_THAN_OPTION,
  olderThanMonth: number,
  olderThanYear: number,
) {
  const allPostsMsg = `Deleted ${deletedCount} posts`
  const olderThanPostsMsg =
    `Deleted ${deletedCount} posts made before ` +
    `${MONTHS[olderThanMonth]} 1, ${olderThanYear}`

  let result: { title: string; detail?: string } | undefined
  if (deletedCount === 0) {
    result = {
      title: 'No posts deleted',
      detail: 'We were not able to find any posts to delete on your profile',
    }
  } else {
    const dateText =
      dateOption !== OLDER_THAN_OPTION.NOW
        ? ` made before ${MONTHS[olderThanMonth]} 1, ${olderThanYear}`
        : ''
    result = {
      title: `You deleted ${deletedCount} post${deletedCount === 1 ? '' : 's'}${dateText}.`,
    }
  }

  return {
    count: totalCount,
    message:
      dateOption !== OLDER_THAN_OPTION.NOW ? olderThanPostsMsg : allPostsMsg,
    result,
  }
}

function getDeleteOlderThanTimestamp(
  olderThanMonth: number,
  olderThanYear: number,
): number {
  const olderThanTimestamp = new Date(olderThanYear, olderThanMonth).getTime()
  _debug(
    `olderThanTimestamp: ${olderThanTimestamp}`,
    'linkedinDeletePostsBefore.getDeleteOlderThanTimestamp',
    'd',
  )
  return olderThanTimestamp
}

async function setUpdateStageStatus(
  dateOption: OLDER_THAN_OPTION,
  olderThanMonth: number,
  olderThanYear: number,
  deletedCount: number,
): Promise<void> {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.LINKEDIN)

  const allPostsMsg = `${BuildConfig.TENANT_NAME} is deleting your LinkedIn posts...`
  const olderThanPostsMsg =
    `${BuildConfig.TENANT_NAME} is deleting LinkedIn posts you made before ` +
    `${MONTHS[olderThanMonth]} 1, ${olderThanYear}`

  await setStoredMachineOverlayStatus(PLATFORM.LINKEDIN, {
    ...currentStatus,
    title:
      dateOption !== OLDER_THAN_OPTION.NOW ? olderThanPostsMsg : allPostsMsg,
    nDeleted: deletedCount,
  })
}

// Export required functions
export const getCurrentValueFn = () => () => ''
export const getIsDisabledFn = () => defaultIsDisabledFn
export const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn
export const getCantUndoFn = () => defaultCantUndoFn
export const getIsFulfilledFn = () => () => false

// No gather or undo stages for content deletion tasks
export const gatherStageDefs: StageDef[] = []
export const undoStageDefs: StageDef[] = []

// Main automation stages
export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_DELETE_POSTS',
    status: AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: linkedinUrls.profileActivityUrl,
    timeout: NO_TIMEOUT,
    action: async (_machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        settingsSelected,
        updateStoredInitialValues,
      } = userContext
      const { resumeData } = additionalMachineData

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        LinkedinSettingKey.DELETE_LINKEDIN_POSTS_BEFORE,
      )
      const dateSettings =
        settingsSelected?.[LinkedinSettingKey.DELETE_LINKEDIN_POSTS_BEFORE]

      const selectedDateOption =
        dateSettings?.dateOption || OLDER_THAN_OPTION.NOW
      const [olderThanMonth, olderThanYear] = getOlderThanMonthYear(
        selectedDateOption,
        dateSettings?.sinceDateMonth || new Date().getMonth().toString(),
        dateSettings?.sinceDateYear || new Date().getFullYear().toString(),
      )
      const olderThanTimestamp = getDeleteOlderThanTimestamp(
        olderThanMonth,
        olderThanYear,
      )

      const postsIterator = getPostsIterator(olderThanTimestamp)

      let totalPostCount = initialValues?.count || 0
      let currentCount = resumeData?.data?.deletedCount || 0

      for await (const post of postsIterator) {
        const urn = post.dataset.urn
        try {
          await deletePost(post, isDry)
        } catch (error) {
          _debug(
            `Error deleting LinkedIn post ${urn}: ${error.message}`,
            'linkedinDeletePostsBefore.update',
            'e',
          )
        }

        totalPostCount += 1
        currentCount += 1

        // Update final result message
        await updateStoredInitialValues(
          {
            [LinkedinSettingKey.DELETE_LINKEDIN_POSTS_BEFORE]:
              buildResultObject(
                totalPostCount,
                currentCount,
                selectedDateOption,
                olderThanMonth,
                olderThanYear,
              ),
          },
          LinkedinRecommendationKey.LINKEDIN_DELETE_POSTS_BEFORE,
        )

        // Update count stats and save progress in case of pause
        await incrementTotalCount(
          LinkedinRecommendationKey.LINKEDIN_DELETE_POSTS_BEFORE,
        )

        await setUpdateStageStatus(
          selectedDateOption,
          olderThanMonth,
          olderThanYear,
          currentCount,
        )

        await resumeData.set({
          ...resumeData.data,
          deletedCount: currentCount,
        })
      }

      // Catches case where there were no posts to delete
      await updateStoredInitialValues(
        {
          [LinkedinSettingKey.DELETE_LINKEDIN_POSTS_BEFORE]: buildResultObject(
            totalPostCount,
            currentCount,
            selectedDateOption,
            olderThanMonth,
            olderThanYear,
          ),
        },
        LinkedinRecommendationKey.LINKEDIN_DELETE_POSTS_BEFORE,
      )

      await userContext.updateStatisticsLastCount(
        LinkedinRecommendationKey.LINKEDIN_DELETE_POSTS_BEFORE,
        currentCount,
      )
    },
  },
]
